import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
import PaymentForm from './PaymentForm.jsx';
import Loading from '../Loading';
import { Radio } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import * as Toast from '../../lib/Toast.js';
import DotDotDotMenu from '../DotDotDotMenu';
import useDelete from '../../lib/useDelete.jsx';
import { ucfirst } from '../../lib/Utils.js';
import { formatDate } from '../../lib/DateTime.js';
import { CheckIcon } from '@heroicons/react/24/outline';
import SavedPaymentMethodRowDetails from '~/components/payments/SavedPaymentMethodRowDetails';

const RadioRow = ({ isChecked, onChange, title, description, children }) => {
    return (
        <div className="flex panel p-4 mb-2">
            <div className="mr-3">
                <input
                    type="radio"
                    name="saved-payment-method"
                    checked={isChecked}
                    onChange={e => onChange(e.target.checked)}
                />
            </div>
            <div className="flex-1">
                <div className="cursor-pointer" onClick={() => onChange(true)}>
                    {title}
                    {!!description && (
                        <div className="text-gray-500 text-sm">
                            {description}
                        </div>
                    )}
                </div>
                <div>{isChecked && children}</div>
            </div>
        </div>
    );
};

const SavedPaymentMethodRow = ({
    paymentMethod,
    isSavedForObject,
    isChecked,
    onChange,
    type,
    id,
    refetch,
    amount,
    onClose,
}) => {
    const [isDeleted, setIsDeleted] = useState(false);

    const { save, isSaving } = useBusinessPostApi(
        `/payments/saved-method/${paymentMethod.id}/charge`,
    );

    const navigate = useNavigate();

    function doSave() {
        save({
            objectType: type,
            objectId: id,
            amount: amount,
        }).then(result => {
            Toast.success(result.success);
            console.log('Redirect:', result);
            navigate(result.redirectTo);
            onClose();
        });
    }

    if (isDeleted) {
        return null;
    }

    return (
        <RadioRow
            isChecked={isChecked}
            onChange={onChange}
            title={
                <SavedPaymentMethodRowDetails
                    paymentMethod={paymentMethod}
                    setIsDeleted={setIsDeleted}
                    refetch={refetch}
                    isPreviouslyUsed={isSavedForObject}
                />
            }
        >
            <button
                type="button"
                className="btn-save mt-2"
                onClick={() => doSave()}
            >
                {isSaving ? 'Paying...' : 'Pay with this card'}
            </button>
        </RadioRow>
    );
};

const PaymentWithProcessor = ({ objectId, objectType, amount, onClose }) => {
    const { save: saveSettings, isSaving: isGettingSettings } =
        useBusinessPostApi(`/payments/payment-start/${objectType}/${objectId}`);

    // Debounce in case they're typing so we don't keep sending requests
    const debouncedAmount = useDebounce(amount, 500);
    const [paymentSettings, setPaymentSettings] = useState();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [shouldVault, setShouldVault] = useState(false);

    function getPaymentSettings() {
        if (parseFloat(amount) > 0) {
            console.log('getting settings for', amount);
            setPaymentSettings(null);
            saveSettings({
                amount: amount,
                vault: shouldVault,
            })?.then(result => {
                setPaymentSettings(result);
                // setShowPaymentForm(true);
            });
        }
    }

    function isSavedForObject(paymentMethod) {
        const objectMethods = paymentSettings?.objectSavedPaymentMethods || [];
        return !!objectMethods.find(p => p.id === paymentMethod.id);
    }

    useEffect(() => {
        //setShowPaymentForm(false);
        getPaymentSettings();
    }, [debouncedAmount, shouldVault]);

    const VaultCardCheckbox = () => {
        return (
            <label className="flex items-center">
                <input
                    type="checkbox"
                    className="mr-2"
                    checked={shouldVault}
                    onChange={e => setShouldVault(e.target.checked)}
                />
                Save card on file
            </label>
        );
    };

    return (
        <div>
            {!!paymentSettings &&
                paymentSettings?.allSavedPaymentMethods?.length > 0 && (
                    // Show the saved payment methods or entering a new card
                    <div>
                        {paymentSettings?.allSavedPaymentMethods.map(
                            paymentMethod => (
                                <SavedPaymentMethodRow
                                    key={paymentMethod.id}
                                    paymentMethod={paymentMethod}
                                    type={objectType}
                                    id={objectId}
                                    onClose={onClose}
                                    isSavedForObject={isSavedForObject(
                                        paymentMethod,
                                    )}
                                    refetch={getPaymentSettings}
                                    amount={amount}
                                    isChecked={
                                        selectedPaymentMethod ===
                                        paymentMethod.id
                                    }
                                    onChange={isChecked =>
                                        isChecked &&
                                        setSelectedPaymentMethod(
                                            paymentMethod.id,
                                        )
                                    }
                                />
                            ),
                        )}
                        <RadioRow
                            isChecked={selectedPaymentMethod === 'new'}
                            title="New Card"
                            onChange={isChecked =>
                                isChecked && setSelectedPaymentMethod('new')
                            }
                        >
                            <div>
                                <VaultCardCheckbox />
                                <PaymentForm
                                    id={objectId}
                                    type={objectType}
                                    paymentSettings={paymentSettings}
                                />
                            </div>
                        </RadioRow>
                    </div>
                )}
            {!!paymentSettings &&
                !paymentSettings?.allSavedPaymentMethods?.length && (
                    // Show the payment form directly since there's nothing saved
                    <div>
                        <VaultCardCheckbox />
                        <PaymentForm
                            id={objectId}
                            type={objectType}
                            paymentSettings={paymentSettings}
                        />
                    </div>
                )}
            {!paymentSettings && <Loading />}
        </div>
    );
};

export default PaymentWithProcessor;
